function gaNotLoaded() {
    return typeof window.gtag === 'undefined'
}

/**
 * Send a page view hit to GA4
 * @param  {string} urlPath - The URL path of the page.
 * @param  {string} measurementId - The measurement ID for the GA4 property.
 * @param  {string} pageTitle - The title of the page.
 */
function pageView(urlPath, measurementId, pageTitle) {
    if (gaNotLoaded()) return;
    const path = urlPath || document.location.pathname;
    window.gtag('event', 'page_view', {
        page_location: path,
        page_title: pageTitle
    });
}

/**
 * Identify a visitor by Id
 * @param  {string} id - unique visitor ID
 * @param  {string} measurementId - The GA4 MeasurementId
 */
export function identifyVisitor(id, measurementId) {
    if (gaNotLoaded()) return;
    if (id) window.gtag('set', 'user_id', id);
    window.gtag('config', measurementId, { send_page_view: false });
}

/**
 * Send event tracking to Google Analytics
 *
 * Stolen from: analytics-plugin-google-analytics/src/browser.js
 * Modified to take trackerName
 *
 * @param  {object} eventData - GA event payload
 * @param  {string} [eventData.event_data = {}]
 * @param  {string} [eventData.event = string]
 * @param  {string} [eventData.nonInteraction = false] - nonInteraction https://bit.ly/2CUzeoz
 * @param  {string} trackerName - The name of the tracker for Google Analytics. Needed when using multiple trackers.
 * @return {object} sent data
 */
function trackEvent(eventData) {
    if (gaNotLoaded()) return;


    const data = {
        ...eventData.event_data,
        nonInteraction: (eventData.nonInteraction !== undefined) ? !!eventData.nonInteraction : false
    };

    /* Attach campaign data */
    const campaignData = eventData.campaign || {};
    const {name, source, medium, content, keyword} = campaignData;
    if (name) data.campaignName = name;
    if (source) data.campaignSource = source;
    if (medium) data.campaignMedium = medium;
    if (content) data.campaignContent = content;
    if (keyword) data.campaignKeyword = keyword;

    /* Send data to Google Analytics */
    window.gtag('event', eventData.event, data);
    return data
}

/**
 * Set any custom dimensions found in the data.
 *
 * @param config - The analytics config object.
 * @param data - The event payload containing the custom dimensions to be set.
 */
function setCustomDimensions(config, data) {
    if (config.dimensions) {
        // Convert the payload key to its dimension name within GA as defined in WebsiteSettings.
        // e.g. 'rate_card': 'Staff' -> 'ga_rate_card': 'Staff'
        const custom_dimensions = {}
        for (let [key, value] of Object.entries(data)) {
            if (key in config.dimensions) {
                // Convert array values to a comma separated string
                if (Array.isArray(value)) value = value.join(',');
                custom_dimensions[config.dimensions[key]] = value;
            }
        }

        window.gtag('set', custom_dimensions)
    }
}

/**
 * Custom implementation of the Google Analytics plugin for the Analytics library.
 *
 * Re-implements all of the provided Google Analytics functionality so that we can support multiple trackers.
 *
 * For custom dimensions to work the plugin must pass be passed a 'dimensions' key in the config. This
 * is a mapping of keys to their appropriate dimension index.
 *
 * When multiple plugins are defined the first plugin will use tracker name `t0` and each subsequent plugin will
 * use an incremented tracker name. So a second plugin instance would use a name of `t1`.
 *
 * For this call to map correctly in Google analytics:
 * ```js
 * analytics.identify('userId12', { 'user_status': Anonymous });
 * ```
 *
 * This plugin would need to be configured like:
 * ```js
 * anvilGoogleAnalytics({
 *     trackingId: 'someID',
 *     dimensions: {
 *         'user_status': 'dimension1',
 *     }
 * });
 * ```
 *
 * Any traits passed to `identify()` will be ignored if they are not defined in the `dimensions` config.
 *
 * @param pluginConfig - dictionary containing the information needed to create the FB pixel.
 * @param pluginConfig.measurementId: The Google analytics tracking ID.
 * @param pluginConfig.dimensions: A mapping of page/user fields to their corresponding google analytics dimension/metric name.
 * @param pluginConfig.pageMeta: Information about the page events are being fired on.
 */
export default function anvilGoogleAnalytics(pluginConfig) {

    return {
        // Name needs to be different so that the analytics initialization doesn't complain about duplicate plugins
        name: `google-analytics-4`,
        config: pluginConfig,
        initialize: ({config}) => {
            if (!config.measurementId) throw new Error('No google analytics measurementId defined');
            if (gaNotLoaded()) {
                let script = document.createElement('script');
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurementId}`;
                document.head.appendChild(script);

                script.onload = function () {
                    // Add gtag config
                    window.dataLayer = window.dataLayer || [];
                    window.gtag = function (...args) {
                        window.dataLayer.push(args);
                    };
                    window.gtag('js', new Date());
                    let gtag_conf = {};
                    if (config.debug) {
                        gtag_conf.debug_mode = false;
                    }
                    window.gtag('config', config.measurementId, gtag_conf);
                }
            }
        },
        page: ({payload, config}) => {
            const {properties} = payload;
            const allPageData = JSON.parse(JSON.stringify(properties));

            // Set any custom dimensions found in the data before sending the page view
            setCustomDimensions(config, allPageData);

            // Stolen from: analytics-plugin-google-analytics/src/browser.js
            pageView(properties.path, config.measurementId, properties.page_title);
        },
        identify: ({payload, config}) => {
            // Only set the userId if one was provided and it wasn't for an anonymous user
            // This is so that we can take advantage of Google Analytics session unification.
            // https://support.google.com/analytics/answer/3123666#SetUpUserID
            if (payload.userId && !payload.userId.startsWith('anon-')) {
                identifyVisitor(payload.userId, config.measurementId);
            }

            setCustomDimensions(config, payload.traits);
        },
        track: ({payload, instance}) => {
            // Stolen from: analytics-plugin-google-analytics/src/browser.js
            // Modified to use trackerName and to standardize events
            const {properties} = payload;
            let {event} = payload;
            let {value, nonInteraction, products, product} = properties;
            const campaign = instance.getState('context.campaign');
            let action = event;
            let event_data;
            let login_method;

            /**
             * Map our events to the correct google analytics events.
             *
             * For events that are a part of the order tunnel will also send the correct ecommerce information.
             *
             * See `analytics/README.md` for details on the events.
             */
            switch (event) {
                case 'AddPaymentInfo':
                    action = "add_payment_info";

                    event_data = {
                        currency: 'USD',
                        value: parseFloat(properties.total),
                        payment_type: properties.payment_method,
                        items: [{
                            item_id: products[0].id,
                            item_name: products[0].name,
                            affiliation: products[0].store,
                            price: parseFloat(properties.total),
                            quantity: 1,
                            item_category: products[0].category
                        }],
                        step: 2,
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'AddToCart':
                    action = "add_to_cart";
                    event_data = {
                        currency: 'USD',
                        value: parseFloat(product.price),
                        items: [{
                            item_id: product.id,
                            item_name: product.name,
                            price: parseFloat(product.price),
                            quantity: 1,
                            item_category: product.category,
                            index: product.position
                        }],
                        step: 1,
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'CompleteLogin':
                    if (properties.login_method.toUpperCase() === 'DIRECT') {
                        login_method = 'Auth';
                    } else {
                        // Social log in
                        login_method = properties.login_method;
                    }
                    action = 'login';

                    event_data = {
                        method: login_method,
                        social_login: login_method !== 'Auth',
                        page: properties.page,
                        event_category: 'Auth'
                    }
                    break;
                case 'CompleteLogout':
                    action = 'logout';
                    event_data = {
                        event_category: 'Auth'
                    }
                    break;
                case 'CompleteRegister':
                    action = 'sign_up';

                    event_data = {
                        method: properties.login_method,
                        page: properties.page
                    }
                    break;
                case 'CompleteRegistrationWall':
                    action = 'complete_registration_wall';
                    nonInteraction = true;
                    event_data = {
                        method: properties.method,
                        page: properties.page,
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'CompletePasswordResetConfirm':
                    action = 'complete_password_reset_confirm';
                    nonInteraction = true;
                    event_data = {
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'DisplayReadPrompt':
                    action = 'display_read_prompt';
                    nonInteraction = true;
                    event_data = {
                        event_category: 'Order Tunnel',
                        page: properties['page'],
                        views_remaining: properties['views_remaining']
                    }
                    break;
                case 'DisplayRegistrationWall':
                    action = 'display_registration_wall';
                    nonInteraction = true;
                    event_data = {
                        event_category: 'Order Tunnel',
                        page: properties['page']
                    }
                    break;
                case 'DisplaySubscribePrompt':
                    action = 'display_subscribe_prompt';
                    nonInteraction = true;

                    event_data = {
                        event_category: 'Order Tunnel',
                        page: properties['page'],
                        views_remaining: properties['views_remaining']
                    }
                    break;
                case 'FormSubmitInvalid': {
                    action = "form_submit_invalid";
                    const error_dict = properties.errors;
                    let error_messages_string = "";
                    let error_count = 0;
                    if (error_dict) {
                        // Turn the dictionary of errors into a comma separated string of the error messages grouped by the field
                        // name where different field error messages are separated by a semicolon. This is then used for the label.
                        // Also total the number of errors to use as the value for the event.
                        for (let [field_name, error_list] of Object.entries(error_dict)) {
                            // Separate different field messages with a semicolon.
                            if (error_messages_string !== "") {
                                error_messages_string += ";"
                            }
                            error_messages_string += `${field_name}:`;
                            error_messages_string += error_list.join();
                            error_count += error_list.length;
                        }
                    }
                    event_data = {}
                    if (error_messages_string) {
                        event_data['message'] = error_messages_string
                    }
                    event_data['error_count'] = error_count
                    break;
                }
                case 'LoadedContentView':
                    action = 'loaded_content_view'
                    nonInteraction = true;
                    event_data = {
                        event_category: 'Infinite Scroll',
                        page: properties.page,
                        loaded_content_number: properties.loaded_content_number
                    }
                    break;
                case 'MailchimpSignup': {
                    action = 'newsletter_sign_up';
                    let mcEmailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (mcEmailSignupNames !== '') {
                                mcEmailSignupNames += ',';
                            }
                            mcEmailSignupNames += signupName;
                        }
                    }

                    event_data = {
                        event_category: 'Mailchimp',
                        signup_names: mcEmailSignupNames
                    }
                    break;
                }
                case 'PrintSubscriberRequest':
                    if (!Object.prototype.hasOwnProperty.call(properties, 'is_print_subscriber')) {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    if (properties.is_print_subscriber !== 'on') {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    action = 'print_subscriber_request';

                    event_data = {
                        event_category: 'Auth'
                    }
                    break;
                case 'Purchase':
                    action = "purchase";
                    event_data = {
                        transaction_id: properties.order_id,
                        currency: 'USD',
                        value: parseFloat(properties.total),
                        event_category: 'Order Tunnel',
                        items: [{
                            item_id: products[0].id,
                            item_name: products[0].name,
                            affiliation: properties.store
                        }],
                        payment_method: properties.payment_method,
                        step: 3
                    }
                    break;
                case 'RevealArticle':
                    action = 'reveal_article';
                    event_data = {
                        event_category: 'Order Tunnel',
                        page: properties.page,
                        views_remaining: properties.views_remaining
                    }
                    break;
                case 'Search':
                    action = "search";
                    event_data = {
                        search_term: properties.query,
                        results_count: properties.results_count,
                        event_category: 'Search',
                    }
                    break;
                case 'SecondStreetSignup': {
                    action = 'newsletter_sign_up';
                    let ssEmailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (ssEmailSignupNames !== '') {
                                ssEmailSignupNames += ',';
                            }
                            ssEmailSignupNames += signupName
                        }
                    }

                    event_data = {
                        event_category: 'SecondStreet',
                        signup_names: ssEmailSignupNames
                    }
                    break;
                }
                case 'Share':
                    event_data = {
                        method: properties.method,
                        item_id: properties.url,
                        event_category: 'Share'
                    }
                    break;
                case 'ShowOrderTunnelRegistration':
                    action = "show_order_tunnel_registration";
                    event_data = {
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'SubmitRegistrationWall':
                    action = 'submit_registration_wall';
                    event_data = {
                        page: properties.page,
                        method: properties.method,
                        event_category: 'Order Tunnel'
                    }
                    break;
                case 'ViewProducts':
                    action = 'view_items_list';
                    // eslint-disable-next-line no-case-declarations
                    let items = []
                    if (products) {
                        for (let prod of products) {
                            let couponNames = [];
                            if (prod.trial) {
                                couponNames.push('trial');
                            }
                            if (prod.discount) {
                                couponNames.push('discount');
                            }
                            const productDetails = {
                                item_id: prod.id,
                                item_name: prod.name,
                                item_category: `${prod.type}/${prod.category}`,
                                price: parseFloat(prod.price),
                                quantity: parseInt(prod.quantity),
                                index: prod.position,
                                coupon: couponNames.join()
                            };

                            items.push(productDetails)
                        }
                    }

                    event_data = {
                        items: items,
                        step: 0,
                        event_category: 'Order Tunnel'
                    }
                    break;
                case  'AdImpression':
                    action = 'impression';
                    nonInteraction = true;
                    event_data = {
                        ad_unit_path: `${properties.ad_unit_path}`,
                        event_category: 'Banner Ad'
                    }
                    break;
                default:
                    break;
            }

            trackEvent({
                event: action,
                value: value,
                nonInteraction,
                campaign: campaign,
                event_data: event_data
            });
        },
        loaded: () => {
            return !gaNotLoaded();
        }
    };
}